import React from "react";
import formatCurrency from "constants/formatCurrency";
import { FILES_URL } from "helpers/url_helper";

/* =========================
   BADGE COLORS
========================= */

const BADGE_COLORS = {
    ACTIVE: "#5FD38D",
    LEASE: "#F59E0B",
    NON_LEASE: "#64748B",
    "MOUNA SEKKATE": "#A855F7",
    "BOLOR ULZII": "#3B82F6",
    "SIMO SEKKATE": "#22C55E",
    DEFAULT: "#6B7280",
};

/* =========================
   SMALL UI PARTS
========================= */

const Badge = ({ label, color }) => (
    <span
        style={{
            padding: "6px 14px",
            borderRadius: 999,
            backgroundColor: color,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
        }}
    >
        {label}
    </span>
);

const Row = ({ label, value, negative }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
            color: negative ? "#F87171" : "#E5E7EB",
            fontSize: 14,
        }}
    >
        <span>{label}</span>
        <span>${formatCurrency(value || 0)}</span>
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
        <div
            style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 12,
                color: "#F9FAFB",
                opacity: 0.9,
            }}
        >
            {title}
        </div>
        {children}
    </div>
);

/* =========================
   MAIN CARD
========================= */

const LocationExpandableCard = ({
    location,
    data,
    expanded,
    onToggle,
}) => {
    const managerKey = (data.account_manager || "").toUpperCase();
    const managerColor = BADGE_COLORS[managerKey] || BADGE_COLORS.DEFAULT;

    const isLease = data.operating_structure === "Lease";

    let dailyLeaseAmount = 0;
    if (isLease) {
        if (data.location_hours && data.start_date) {
            const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
            const hoursLower = data.location_hours.toLowerCase();
            const isOpenOnDay = {};

            // 1. Determine which weekdays are open based on location_hours string
            daysOfWeek.forEach(day => {
                const line = hoursLower.split("\n").find(l => l.includes(day));
                isOpenOnDay[day] = line && !line.includes("closed");
            });

            // 2. Iterate through the entire month of the report start_date to count actual open days
            const d = new Date(data.start_date);
            const year = d.getFullYear();
            const month = d.getMonth();

            const dateIterator = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);

            let openDays = 0;
            while (dateIterator <= lastDay) {
                const dayName = daysOfWeek[dateIterator.getDay()];
                if (isOpenOnDay[dayName]) openDays++;
                dateIterator.setDate(dateIterator.getDate() + 1);
            }

            dailyLeaseAmount = (data.daily_lease_amount || 0) * openDays;
        } else {
            // Fallback estimate if hours or date are missing
            dailyLeaseAmount = (data.daily_lease_amount || 0) * 30;
        }
    }

    const parkingLotCost =
        data.costs?.payments
            ?.filter(p => p.type === "Parking Lot Cost")
            ?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

    const totalBillables = (data.fees?.amount || 0);
    const totalLabor = (data.prospr?.cost || 0);
    const totalCosts = (data.costs?.amount || 0);

    // Header Payables = Total Costs - Parking Lot Expenses (as requested)
    const headerPayables = totalCosts - parkingLotCost;

    const sumFeeByType = (type) =>
        (data.fees?.payments || [])
            .filter(p => p.type === type)
            .reduce((sum, p) => sum + (p.amount || 0), 0);

    const sumCostByType = (type) =>
        (data.costs?.payments || [])
            .filter(p => p.type === type)
            .reduce((sum, p) => sum + (p.amount || 0), 0);

    const billableFeesTotal =
        sumFeeByType("Management Fee") +
        sumFeeByType("Insurance Fee") +
        sumFeeByType("Tech Fee") +
        sumFeeByType("Ops Supplies Fee") +
        sumFeeByType("Permit Meter Fee") +
        sumFeeByType("Parking Lot Fee");

    const hasPayableCosts = (totalLabor + totalCosts) > 0;

    const payableCostsTotal =
        sumCostByType("Parking Lot Cost") +
        sumCostByType("Partner Remittance Fee");

    const totalThirdPartyIncome =
        (data.corepark?.cash || 0) +
        (data.corepark?.card || 0) +
        (data.spothero?.amount || 0) +
        (data.surepark?.amount || 0) +
        (data.resolve?.amount || 0) +
        (data.events?.income || 0) +
        (data.monthly_parking_orders?.amount || 0);

    const sourceTotals = [
        { key: "corepark", label: "CorePark", amount: (data.corepark?.cash || 0) + (data.corepark?.card || 0) },
        { key: "spothero", label: "SpotHero", amount: data.spothero?.amount || 0 },
        { key: "surepark", label: "SurePark", amount: data.surepark?.amount || 0 },
        { key: "resolve", label: "Resolve", amount: data.resolve?.amount || 0 },
        { key: "monthly", label: "Monthly Parking", amount: data.monthly_parking_orders?.amount || 0 },
        { key: "events", label: "Events", amount: data.events?.income || 0 },
    ].filter(s => s.amount > 0);

    return (
        <div
            style={{
                backgroundColor: "rgb(58, 58, 58)",
                borderRadius: 16,
                marginBottom: 20,
                overflow: "hidden",
                boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
            }}
        >
            {/* HEADER */}
            <div
                onClick={onToggle}
                style={{
                    padding: 20,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <img
                        src={
                            data.logo
                                ? `${FILES_URL}${data.logo}`
                                : "https://placehold.co/80x80"
                        }
                        alt="logo"
                        style={{
                            height: 52,
                            width: 52,
                            borderRadius: 10,
                            backgroundColor: "#1f1f1f",
                            objectFit: "contain",
                        }}
                    />

                    <div>
                        <div
                            style={{
                                fontSize: 18,
                                fontWeight: 800,
                                color: "#F9FAFB",
                                marginBottom: 8,
                            }}
                        >
                            {location}
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <Badge
                                label={data.account_manager || "No Manager"}
                                color={managerColor}
                            />
                            <Badge
                                label={isLease ? "LEASE" : "NON LEASE"}
                                color={isLease ? BADGE_COLORS.LEASE : BADGE_COLORS.NON_LEASE}
                            />
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        gap: 40,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    {/* TOTAL 3RD PARTY INCOME */}
                    {totalThirdPartyIncome > 0 &&
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                TOTAL DATA SOURCE INCOME
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#60A5FA",
                                }}
                            >
                                ${formatCurrency(totalThirdPartyIncome)}
                            </div>
                        </div>
                    }

                    {/* TOTAL BILLABLES */}
                    {totalBillables > 0 &&
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                TOTAL BILLABLES
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#22C55E",
                                }}
                            >
                                ${formatCurrency(totalBillables)}
                            </div>
                        </div>
                    }

                    {/* TOTAL LABOR */}
                    {totalLabor > 0 &&
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                TOTAL LABOR
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#fb923c",
                                }}
                            >
                                ${formatCurrency(totalLabor)}
                            </div>
                        </div>
                    }

                    {/* TOTAL PAYABLES (Minus Lot Expenses) */}
                    {headerPayables > 0 &&
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                TOTAL PAYABLES
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#f472b6",
                                }}
                            >
                                ${formatCurrency(headerPayables)}
                            </div>
                        </div>
                    }

                    {/* DAILY LEASE */}
                    {isLease && (
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                MONTHLY EXPECTED LEASE
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#FBBF24",
                                }}
                            >
                                ${formatCurrency(dailyLeaseAmount)}
                            </div>
                        </div>
                    )}

                    {/* PARKING LOT COST */}
                    {parkingLotCost > 0 && (
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "#D1D5DB",
                                    marginBottom: 6,
                                }}
                            >
                                LOT EXPENSE
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#F87171",
                                }}
                            >
                                ${formatCurrency(parkingLotCost)}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* EXPANDED */}
            {expanded && (
                <div
                    style={{
                        padding: "20px 22px",
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        backgroundColor: "rgba(0,0,0,0.25)",
                    }}
                >
                    {sourceTotals.length > 0 && (
                        <div
                            style={{
                                backgroundColor: "rgba(0,0,0,0.25)",
                                borderRadius: 12,
                                padding: "14px 16px",
                                marginBottom: 16,
                                display: "flex",
                                gap: 14,
                                flexWrap: "wrap",
                            }}
                        >
                            {sourceTotals.map(src => (
                                <div
                                    key={src.key}
                                    style={{
                                        backgroundColor: "rgba(0,0,0,0.35)",
                                        borderRadius: 10,
                                        padding: "10px 14px",
                                        minWidth: 140,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 12,
                                            opacity: 0.7,
                                            color: "#D1D5DB",
                                            marginBottom: 4,
                                        }}
                                    >
                                        {src.label}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 16,
                                            fontWeight: 800,
                                            color: "#22C55E",
                                        }}
                                    >
                                        ${formatCurrency(src.amount)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div
                        style={{
                            backgroundColor: "rgba(0,0,0,0.25)",
                            borderRadius: 12,
                            padding: "16px 18px",
                            marginBottom: 20,
                        }}
                    >
                        {billableFeesTotal === 0 ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    fontWeight: 800,
                                    fontSize: 16,
                                    color: "#F9FAFB",
                                    padding: "20px 0",
                                }}
                            >
                                No Billable Fees
                            </div>
                        ) : (
                            <Section title="Billable Fees">
                                <Row label="Management Fee" value={sumFeeByType("Management Fee")} />
                                <Row label="Insurance Fee" value={sumFeeByType("Insurance Fee")} />
                                <Row label="Tech Fee" value={sumFeeByType("Tech Fee")} />
                                <Row label="Ops Supplies" value={sumFeeByType("Ops Supplies Fee")} />
                                <Row label="Permit / Meter Fee" value={sumFeeByType("Permit Meter Fee")} />
                                <Row label="Parking Lot Fee" value={sumFeeByType("Parking Lot Fee")} />
                            </Section>
                        )}
                    </div>

                    <div
                        style={{
                            backgroundColor: "rgba(0,0,0,0.25)",
                            borderRadius: 12,
                            padding: "16px 18px",
                            marginBottom: 20,
                        }}
                    >
                        {payableCostsTotal === 0 ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    fontWeight: 800,
                                    fontSize: 16,
                                    color: "#F9FAFB",
                                    padding: "20px 0",
                                }}
                            >
                                No Payable Costs
                            </div>
                        ) : (
                            <Section title="Payable Costs">
                                <Row
                                    label="Parking Lot Cost"
                                    value={sumCostByType("Parking Lot Cost")}
                                    negative
                                />
                                <Row
                                    label="Total Labor"
                                    value={totalLabor}
                                    negative
                                />
                                <Row
                                    label="Partner Remittance Fee"
                                    value={sumCostByType("Partner Remittance Fee")}
                                    negative
                                />
                            </Section>
                        )}
                    </div>

                    {isLease && (
                        <div
                            style={{
                                backgroundColor: "rgba(0,0,0,0.25)",
                                borderRadius: 12,
                                padding: "16px 18px",
                                marginBottom: 20,
                            }}
                        >
                            <Section title="Lease Details">
                                <Row
                                    label="Daily Lease"
                                    value={data.daily_lease_amount}
                                    positive
                                />

                                <Row
                                    label="Lease Settlement"
                                    value={data.lease_payments?.amount}
                                    positive
                                />
                            </Section>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationExpandableCard;